// MACHINERY V2 (Stroje) — decommissioned banner + Restore (M2.4).
//
// Spec: MACHINE_PROFILE_REDESIGN_DATA_PACKAGE.md §1 — "Decommission banner
// present in data pkg, NOT shown in artboard — must carry" (ticket §5 #1). So
// this surface has no designer reference: it is rebuilt in the v2 idiom
// (amber-tinted card, mono date line, italic quoted reason) from the live
// banner's content, per the §9 D4 consistency rule.
//
// THE BANNER OWNS RESTORE (§1: "rendered only inside banner"), which is why
// useProfileActions filters the `restore` entry out of the kebab — one
// affordance, not two. The gate is still the shared one: `onRestore` is null
// unless buildFleetActions produced a restore entry for this user, and the
// backend enforces Write regardless.
//
// IT NOW OWNS THE HARD DELETE TOO (MACHINE_DELETE_SPEC.md §7.6), on exactly the
// same terms: `onDelete` is null unless buildFleetActions produced the entry —
// which it does only for an Admin on a decommissioned machine — and the server
// is the authority either way. This banner is the right home for it because the
// banner IS the decommissioned-machine surface: the two exits from that state,
// back (Obnoviť) and out (Zmazať natrvalo), sit side by side and nowhere else.
//
// THE TWO BUTTONS MUST NOT LOOK ALIKE. Restore is the default `.btn`; delete
// re-uses the same atom for geometry + hover but overrides its text and border
// to var(--red) inline. Inline wins over the `.btn:hover` rule's `color`, so the
// destructive control stays red through hover instead of resolving to the same
// ink as its neighbour. That is the same one-token treatment FleetCardMenu gives
// a `danger` row, and it needs no new atom in the 288-line token sheet.
//
// The three content fields are all conditional except the title:
// decommissionedDate -> `decommissionedOn {date}`, decommissionReason -> italic
// quoted line. Both come straight off the DTO — no derivation.
//
// No Tailwind, no shadcn, no MUI, no @emotion, no sx, no dt tokens.
import React from "react";

import useTranslation from "../../../../shared/hooks/useTranslation";
import { Machine } from "../../models/models";

const WarningGlyph: React.FC = () => (
  <svg
    width="18"
    height="18"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="1.7"
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden
  >
    <path d="M12 3.5 21 19.5H3z" />
    <path d="M12 9.5v4.5" />
    <path d="M12 17h.01" />
  </svg>
);

// NO SPINNER, DELIBERATELY — the busy state is `disabled` + a swapped label +
// aria-busy. A rotating SVG here would have to be SMIL (<animateTransform>),
// because a CSS keyframe means editing machinery-v2-tokens.scss at 288 of its
// 300 lines. And SMIL is NOT covered by that sheet's reduced-motion block: the
// block sets `animation`/`transition` to none, which are CSS properties and do
// not reach a SMIL animation element at all — so a prefers-reduced-motion user
// would keep the spin. "Overujem…" on a disabled button says the same thing
// with no motion and no false claim about what the sheet covers.

export interface ProfileDecommissionBannerProps {
  machine: Machine;
  /** Null when the user has no restore entry — the button then does not render. */
  onRestore: (() => void) | null;
  /** Null unless the user is an Admin AND the machine is decommissioned. */
  onDelete: (() => void) | null;
  /** True from the click until the confirm dialog or a toast appears. This
   *  button is the ONLY delete affordance on the profile, so without it the
   *  eligibility read — which fans out to three services — looks like a dead
   *  button (MDEL-FE review finding 2). */
  deleteBusy?: boolean;
}

const ProfileDecommissionBanner: React.FC<ProfileDecommissionBannerProps> = ({
  machine,
  onRestore,
  onDelete,
  deleteBusy,
}) => {
  const { translate, getCurrentLanguage } = useTranslation("myMachinery");

  return (
    <div
      style={{
        padding: "12px 14px",
        borderRadius: 10,
        background: "color-mix(in oklab, var(--amber) 10%, var(--card))",
        border: "1px solid color-mix(in oklab, var(--amber) 30%, var(--border))",
        borderLeft: "3px solid var(--amber)",
        display: "flex",
        alignItems: "flex-start",
        gap: 10,
        minWidth: 0,
      }}
    >
      <span style={{ color: "var(--amber)", flexShrink: 0, display: "inline-flex" }}>
        <WarningGlyph />
      </span>

      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ fontSize: 13.5, fontWeight: 600, color: "var(--ink)" }}>
          {translate("myMachinery:decommissionedBannerTitle")}
        </div>
        {machine.decommissionedDate && (
          <div
            style={{
              fontFamily: "var(--font-mono)",
              fontSize: 11,
              color: "var(--muted)",
              marginTop: 2,
            }}
          >
            {translate("myMachinery:decommissionedOn", {
              date: new Date(machine.decommissionedDate).toLocaleDateString(
                getCurrentLanguage()
              ),
            })}
          </div>
        )}
        {machine.decommissionReason && (
          <div
            style={{
              fontSize: 12.5,
              fontStyle: "italic",
              color: "var(--muted)",
              marginTop: 3,
            }}
          >
            {"„" + machine.decommissionReason + "“"}
          </div>
        )}
      </div>

      {(onRestore || onDelete) && (
        <div style={{ display: "flex", gap: 8, flexShrink: 0 }}>
          {onRestore && (
            <button type="button" className="btn" onClick={onRestore}>
              {translate("myMachinery:restore")}
            </button>
          )}
          {onDelete && (
            <button
              type="button"
              className="btn"
              disabled={deleteBusy === true}
              aria-busy={deleteBusy === true}
              style={{
                color: "var(--red)",
                borderColor: "color-mix(in oklab, var(--red) 42%, var(--border))",
              }}
              onClick={onDelete}
            >
              {translate(
                deleteBusy
                  ? "myMachinery:v2.delete.busyLabel"
                  : "myMachinery:v2.delete.action"
              )}
            </button>
          )}
        </div>
      )}
    </div>
  );
};

export default ProfileDecommissionBanner;
